import { Tag, X } from 'lucide-react';

import { cn } from '../../../lib/cn';

// The fixed 16-colour palette. Keys are mirrored on the backend (ticket-label
// service LABEL_COLORS) — the DB stores the key, both ends resolve it here.
// Chips are solid, saturated (-700) with white text so they read clearly in
// both light and dark mode; the picker swatches stay bright (-500).
export const LABEL_COLORS: Record<string, { chip: string; swatch: string }> = {
    slate: { chip: 'bg-slate-600 text-white', swatch: 'bg-slate-500' },
    red: { chip: 'bg-red-700 text-white', swatch: 'bg-red-500' },
    orange: { chip: 'bg-orange-700 text-white', swatch: 'bg-orange-500' },
    amber: { chip: 'bg-amber-700 text-white', swatch: 'bg-amber-500' },
    yellow: { chip: 'bg-yellow-700 text-white', swatch: 'bg-yellow-400' },
    lime: { chip: 'bg-lime-700 text-white', swatch: 'bg-lime-500' },
    green: { chip: 'bg-green-700 text-white', swatch: 'bg-green-500' },
    emerald: { chip: 'bg-emerald-700 text-white', swatch: 'bg-emerald-500' },
    teal: { chip: 'bg-teal-700 text-white', swatch: 'bg-teal-500' },
    cyan: { chip: 'bg-cyan-700 text-white', swatch: 'bg-cyan-500' },
    sky: { chip: 'bg-sky-700 text-white', swatch: 'bg-sky-500' },
    blue: { chip: 'bg-blue-700 text-white', swatch: 'bg-blue-500' },
    indigo: { chip: 'bg-indigo-700 text-white', swatch: 'bg-indigo-500' },
    violet: { chip: 'bg-violet-700 text-white', swatch: 'bg-violet-500' },
    fuchsia: { chip: 'bg-fuchsia-700 text-white', swatch: 'bg-fuchsia-500' },
    pink: { chip: 'bg-pink-700 text-white', swatch: 'bg-pink-500' },
};

export const LABEL_COLOR_KEYS = Object.keys(LABEL_COLORS);

// Deterministic fallback when a label has no explicit colour set. Hashed on the
// lowercased name so the same label reads the same colour everywhere regardless
// of casing (matching the case-insensitive custom-colour lookup).
function hashKey(s: string): string {
    const lower = s.toLowerCase();
    let h = 0;
    for (let i = 0; i < lower.length; i++) h = (h * 31 + lower.charCodeAt(i)) | 0;
    return LABEL_COLOR_KEYS[Math.abs(h) % LABEL_COLOR_KEYS.length];
}

const SIZES = { sm: 'px-2 py-0.5 text-xs', md: 'px-2.5 py-1 text-sm' } as const;

export function LabelChip({
    label,
    colorKey,
    onRemove,
    onClick,
    size = 'md',
}: {
    label: string;
    /** Palette key; falls back to a stable hash of the label when unset. */
    colorKey?: string | null;
    onRemove?: () => void;
    /** When set, the label body becomes a button (used to open the colour picker). */
    onClick?: () => void;
    size?: keyof typeof SIZES;
}) {
    const key = colorKey && LABEL_COLORS[colorKey] ? colorKey : hashKey(label);
    const body = (
        <>
            <Tag aria-hidden="true" className="h-3 w-3 shrink-0" />
            <span className="truncate">{label}</span>
        </>
    );
    return (
        <span className={cn('inline-flex max-w-full items-center gap-1 rounded-full font-semibold ring-1 ring-inset ring-white/15', SIZES[size], LABEL_COLORS[key].chip)}>
            {onClick ? (
                <button type="button" onClick={onClick} title="Schimba culoarea" className="inline-flex min-w-0 items-center gap-1">
                    {body}
                </button>
            ) : (
                body
            )}
            {onRemove && (
                <button
                    type="button"
                    onClick={onRemove}
                    aria-label={`Sterge eticheta ${label}`}
                    className="-mr-0.5 ml-0.5 shrink-0 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
                >
                    <X aria-hidden="true" className="h-3 w-3" />
                </button>
            )}
        </span>
    );
}
